import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import {
  Card,
  TextField,
  Button,
  Tabs,
  Tab,
  DialogProps,
  InputLabel,
  InputAdornment,
  MenuItem,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useIntl } from 'react-intl';

import _ from 'lodash';
import { TabPanel, a11yProps } from '../../../components/other/Tab/TabPanel';
import Preloader from '../../../components/other/Preloader/Preloader';
import { Modal } from '../../../components/other/Modals';
import AlertDialog from '../../../components/other/Dialog/AlertDialog';
import StoresTable from '../../../components/tableComponents/Table/StoresTable';
import UsersTable from '../../../components/tableComponents/Table/UsersTable';

import homeStyles from '../../../constants/homeStyles';
import { IAppState } from '../../../store/rootDuck';
import { actions as companiesActions } from '../../../store/ducks/companies.duck';
import { actions as profileActions } from '../../../store/ducks/profile.duck';
import { actions as storeActions } from '../../../store/ducks/store.duck';
import { actions as usersActions } from '../../../store/ducks/users.duck';

import { setLayoutFooter, setLayoutSubheader } from '../../../utils/layout';
import useCrudSnackbar from '../../../hooks/useCrudSnackbar';
import { ICompany } from '../../../interfaces/companies';
import CompaniesTable from '../../../components/tableComponents/Table/CompaniesTable';
import { IUser, UserRoles } from '../../../interfaces/user';
import { BUYER_ROLE, UserListType } from '../users/constants';
import { useDefineUserRole, useFormatMessage } from '../../../hooks';
import CkEditorCustom from '../../../components/formComponents/CkEditorCustom';
import CountrySelect from '../../../components/ui/CountrySelect';
import { IMaskPhoneInput } from '../../auth/components/CheckInSystem';
import { useAxiosGetCallback } from '../../../hooks/useAxiosGet';
import { useHandleCountrySelect } from '../../auth/hooks/useHandleCountrySelect';
import { getPublicDoc } from '../../../crud/docs.crud';

const useStyles = makeStyles()(theme => {
  return {
    card: {
      marginTop: theme.spacing(3),
    },
    actions: {
      marginTop: theme.spacing(3),
    },
    buttons: {
      marginRight: theme.spacing(2),
      marginLeft: theme.spacing(2),
    },
    btnDel: {
      // marginLeft: theme.spacing(2),
    },
    delButton: {
      marginLeft: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        marginTop: theme.spacing(2),
      },
    },
    switch: {
      marginLeft: theme.spacing(1),
    },
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      color: '#fff',
    },
    managers: {
      minHeight: 55,
      marginTop: 10,
      padding: theme.spacing(1.5),
    },
    managersTitle: {
      marginBottom: 10,
    },
    rootTable: {
      margin: '20px 0',
      minHeight: 200,
      flex: 1,
      padding: '0 10px',
      [theme.breakpoints.down('md')]: {
        overflowX: 'auto',
        width: '100%',
      },
    },
    table: {
      [theme.breakpoints.down('md')]: {
        overflowX: 'auto',
        overflowY: 'hidden',
        width: '300px',
        whiteSpace: 'nowrap',
        padding: theme.spacing(2),
      },
    },
    dates: {
      display: 'flex',
      flexDirection: 'row',
      [theme.breakpoints.down(470)]: {
        flexDirection: 'column',
      },
    },
    country: {
      marginRight: 5,
      [theme.breakpoints.down(470)]: {
        marginRight: 0,
      },
    },
    fieldsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      // gap: 24,

      [theme.breakpoints.down(1024)]: {
        flexDirection: 'column',
      },
    },
    fields: {
      width: '100%',
      [theme.breakpoints.down(1024)]: {
        width: '100%',
        maxWidth: 880,
      },
    },
    editorContainer: { width: '100%' },
    title: {
      marginBottom: 30,
      fontSize: 34,
      fontWeight: '800',
      fontStyle: 'normal',
      letterSpacing: 'normal',
    },
    mobileActions: {
      display: 'none',
      [theme.breakpoints.down(1024)]: {
        display: 'inline',
      },
    },
    webActions: {
      display: 'inline',
      [theme.breakpoints.down(1024)]: {
        display: 'none',
      },
    },
    editButton: {
      backgroundColor: '#006997',
      marginTop: 16,
    },
  };
});

interface IField<T> {
  title: string;
  field: T;
}

const getInitialValues = (company: ICompany | null, defaultRole?: string) => ({
  name: company?.name || '',
  brand: company?.brand || '',
  inn: company?.inn || '',
  phone_number: company?.phone_number || '',
  site: company?.site || '',
  roles: company ? company?.roles?.[0] : defaultRole || '',
  fee: company?.fee ?? 15,
  deliveryPeriod: company?.delivery_period ?? null,

  requisites: {
    ogrn: company ? company.requisites?.ogrn : '',
    kpp: company ? company.requisites?.kpp : '',
    bank: company ? company.requisites?.bank : '',
    bank_bic: company ? company.requisites?.bank_bic : '',
    correspondent_account: company ? company.requisites?.correspondent_account : '',
    payment_account: company ? company.requisites?.payment_account : '',
    legal_address: company ? company.requisites?.legal_address : '',
    post_address: company ? company.requisites?.post_address : '',
    owner: company ? company.requisites?.owner : '',
    legal_entity: company ? company.requisites?.legal_entity : '',
    reason: company ? company.requisites?.reason : '',
  },
});

interface IDocument {
  code: string;
  name: string;
  text: string;
  public: boolean;
  id?: number;
  locale?: string;
}

const CompanyEditPage: React.FC<TPropsFromRedux> = ({
  loadingMe,
  me,
  fetchMe,
  company,
  fetch,
  loading,
  clearEdit,
  add,
  edit,
  delCompany,
  clearDelCompany,
  delCompanySuccess,
  delCompanyError,
  delCompanyLoading,
  editSuccess,
  byIdSuccess,
  editLoading,
  editError,
  delStore,
  clearDelStore,
  delStoreSuccess,
  delStoreError,
  searchCompany,
  companies,
  loadingCompanies,
  pageCompanies,
  perPageCompanies,
  totalCompanies,
  users,
  perPageUsers,
  pageUsers,
  loadingUsers,
  totalUsers,
  fetchUsers,
  filter,
  clearPagination,
  clearAddCompanyId,
  addCompanyId,
  addSuccess,
  stores,
  pageStores,
  perPageStores,
  totalStores,
  fetchStores,
  loadingStores,
  clearStores,
  clearFetchById,
  byIdError,
}) => {
  const { companyId } = useParams();
  const [open, setOpen] = React.useState(false);
  const [valueTabs, setValueTabs] = useState(0);
  const [deleteStoreId, setDeleteStoreId] = useState<number | undefined>(-1);
  const [unconnectUserId, setUnconnectUserId] = useState<number | undefined>(-1);
  const [openUsersDialog, setOpenUsersDialog] = useState(false);
  const fm = useFormatMessage();

  const [isOpenDeleteStoreAlert, setOpenDeleteStoreAlert] = useState(false);
  const [isOpenUnconnectUserAlert, setIsOpenUnconnectUserAlert] = useState(false);

  const [openDialogCompanies, setOpenDialogCompanies] = useState(false);
  const [editorState, setEditorState] = useState<any>(null);
  const {
    data: countries,
    loading: countriesLoading,
    makeRequest,
  } = useAxiosGetCallback<any>();
  const { countryId, setCountryId, countryData } = useHandleCountrySelect(countries, 1);
  const isManager = useDefineUserRole(me, 'ROLE_MANAGER');
  const isVendor = useDefineUserRole(me, 'ROLE_VENDOR');
  const isBuyer = useDefineUserRole(me, 'ROLE_BUYER');
  const isBuyerCompany = company?.roles?.includes('ROLE_BUYER');
  const isBuyerStaff = useDefineUserRole(me, 'ROLE_BUYER_STAFF');
  const isVendorStaff = useDefineUserRole(me, 'ROLE_VENDOR_STAFF');
  const defaultCompanyRole = isBuyer || isBuyerStaff ? 'ROLE_BUYER' : 'ROLE_VENDOR';
  const [userDocument, setDocument] = useState<IDocument | null>(null);

  const classes1 = useStyles();
  const { classes } = classes1;
  const homeClasses = homeStyles();
  const navigate = useNavigate();
  const intl = useIntl();

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setValues,
    setFieldValue,
  } = useFormik({
    initialValues: getInitialValues(
      company,
      !me?.is_admin && !isManager ? defaultCompanyRole : undefined
    ),
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      roles: Yup.string().required(fm('AUTH.VALIDATION.REQUIRED_FIELD')).nullable(),
      name: Yup.string().required(fm('AUTH.VALIDATION.REQUIRED_FIELD')).nullable(),
      inn: Yup.string().required(fm('AUTH.VALIDATION.REQUIRED_FIELD')).nullable(),
      phone_number: Yup.string()
        .required(fm('AUTH.VALIDATION.REQUIRED_FIELD'))
        .nullable()
        .test('mask-length', fm('AUTH.VALIDATION.REQUIRED_FIELD'), function (value) {
          if (!value) return true;
          const currentMaskLength = countryData?.mask?.replace(/[^*]/g, '').length;
          return value.length === currentMaskLength;
        }),
    }),
    onSubmit: submitValues => {
      let description = '';
      let paymentDetails = '';
      if (editorState) {
        description = editorState?.getData() || '';
      }
      if (companyId) {
        edit({
          id: +companyId,
          data: {
            ...submitValues,
            roles: submitValues?.roles ? [submitValues?.roles] : null,
            description,
            payment_details: paymentDetails,
            countryId: countryData.id || 1,
            deliveryPeriod: submitValues.deliveryPeriod || null,
            requisites: {
              kpp: values.requisites.kpp || '',
              bank: values.requisites.bank || '',
              correspondent_account: values.requisites.correspondent_account || '',
              bank_bic: values.requisites.bank_bic || '',
              legal_address: values.requisites.legal_address || '',
              ogrn: values.requisites.ogrn || '',
              payment_account: values.requisites.payment_account || '',
              post_address: values.requisites.post_address || '',
              owner: values.requisites.owner || '',
              legal_entity: values.requisites.legal_entity || '',
              reason: values.requisites.reason || '',
            },
            fee:
              me?.is_admin && submitValues.roles === UserRoles.ROLE_VENDOR
                ? submitValues.fee
                : undefined,
          },
        });
      } else {
        add({
          ...submitValues,
          roles: submitValues?.roles ? [submitValues?.roles] : null,
          description,
          payment_details: paymentDetails,
          countryId: countryData.id || 1,
          requisites: {
            kpp: values.requisites.kpp || '',
            bank: values.requisites.kpp || '',
            correspondent_account: values.requisites.correspondent_account || '',
            bank_bic: values.requisites.bank_bic || '',
            legal_address: values.requisites.legal_address || '',
            ogrn: values.requisites.ogrn || '',
            payment_account: values.requisites.payment_account || '',
            post_address: values.requisites.post_address || '',
            owner: values.requisites.owner || '',
            legal_entity: values.requisites.legal_entity || '',
            reason: values.requisites.reason || '',
          },
          fee:
            me?.is_admin && submitValues.roles === UserRoles.ROLE_VENDOR
              ? submitValues.fee
              : undefined,
        });
      }
    },
  });

  const getLocalTabs = useCallback(() => {
    if (companyId) {
      const localTabs = localStorage.getItem('companyTab');
      if (localTabs) {
        const { id, tab } = JSON.parse(localTabs);
        if (id && id?.toString() === companyId?.toString()) {
          setValueTabs(tab);
        } else {
          localStorage.removeItem('companyTab');
        }
      }
    }
  }, [companyId]);

  const setLocalTabs = useCallback(
    (newValue: number) => {
      companyId &&
        localStorage.setItem('companyTab', JSON.stringify({ id: +companyId, tab: newValue }));
    },
    [companyId]
  );

  useCrudSnackbar({
    success: false,
    successMessage: '',
    error: byIdError,
    errorMessage: `${intl.formatMessage({ id: 'ERROR' })}: ${byIdError}`,
    afterSuccessOrError: () => {
      navigate(-1);
      clearFetchById();
    },
  });

  // For edit actions
  useCrudSnackbar({
    success: editSuccess,
    error: editError,
    successMessage: companyId
      ? intl.formatMessage({ id: 'COMPANY.EDIT.SUCCESS' })
      : intl.formatMessage({ id: 'COMPANY.CREATE.SUCCESS' }),
    errorMessage: `${intl.formatMessage({ id: 'ERROR' })}: ${editError}`,
    afterSuccessOrError: () => {
      setOpen(false);
    },
    afterSuccess: () => {
      if (!companyId && valueTabs === 0) navigate(-1);
      fetchMe();
    },
  });

  // For delete actions
  useCrudSnackbar({
    success: delCompanySuccess,
    error: delCompanyError,
    clear: clearDelCompany,
    successMessage: intl.formatMessage({ id: 'COMPANY.DELETE.SUCCESS' }),
    errorMessage: `${intl.formatMessage({ id: 'ERROR' })}: ${delCompanyError}`,
    afterSuccessOrError: () => {
      clearDelCompany();
      setOpen(false);
    },
    afterSuccess: () => {
      navigate(-1);
    },
  });

  // For delete stories
  useCrudSnackbar({
    success: delStoreSuccess,
    error: delStoreError,
    clear: clearDelStore,
    successMessage: intl.formatMessage({ id: 'STORE.DELETE.SUCCESS' }),
    errorMessage: `${intl.formatMessage({ id: 'ERROR' })}: ${delStoreError}`,
    afterSuccess: () => {
      if (companyId) {
        fetch(+companyId);
        fetchStores({ page: pageStores, perPage: perPageStores, companyId: +companyId });
      }
    },
    afterSuccessOrError: () => {
      clearDelStore();
    },
  });

  useEffect(() => {
    if (valueTabs !== 0 && (isBuyerStaff || isVendorStaff)) {
      setValueTabs(0);
    }
  }, [valueTabs, isBuyerStaff, isVendorStaff]);

  useEffect(() => {
    getLocalTabs();
    makeRequest('/api/phone_codes');
    fetchMe();
    return () => {
      clearEdit();
      clearStores();
      clearFetchById();
    };
  }, []);

  useEffect(() => {
    if (isVendor) {
      getPublicDoc('marketplace_commission').then(({ data: requestData }) => {
        setDocument(requestData.data);
      });
    }
  }, [isVendor]);

  useEffect(() => {
    if (company?.country?.id) {
      setCountryId(company.country.id);
    }
  }, [company]);

  useEffect(() => {
    if (addSuccess && addCompanyId) {
      if (valueTabs !== 0) {
        navigate(`/companies/edit/${addCompanyId}`, { replace: true });
      }
    }
  }, [addSuccess, addCompanyId]);

  useEffect(() => {
    if (addCompanyId && companyId) {
      clearAddCompanyId();
    }
  }, [addCompanyId, companyId]);

  useEffect(() => {
    editError && valueTabs !== 0 && setValueTabs(0);
  }, [editError]);

  const handleChangeTabPhoto = useCallback(
    (newValue: number) => {
      handleSubmit();
      setValueTabs(newValue);
      setLocalTabs(newValue);
    },
    [handleSubmit, setValueTabs]
  );

  useEffect(() => {
    if (
      me &&
      !me.is_admin &&
      editError &&
      editError === intl.formatMessage({ id: 'COMPANY.ERROR.UNIQUEINN' }) &&
      !companyId
    ) {
      searchCompany({ page: 1, perPage: 20, inn: values.inn });
      setOpenDialogCompanies(true);
    }
  }, [me, editError, companyId, addCompanyId, addSuccess]);

  useEffect(() => {
    if (companyId) {
      fetch(+companyId);
    }
  }, [fetch, companyId]);

  useEffect(() => {
    if (companyId) {
      fetchStores({ page: pageStores, perPage: perPageStores, companyId: +companyId });
    }
  }, [fetchStores, perPageStores, pageStores, me, companyId]);

  setLayoutSubheader({
    title: companyId
      ? `${intl.formatMessage({ id: 'COMPANY.EDIT.TITLE' })}`
      : `${intl.formatMessage({ id: 'COMPANY.ADD.TITLE' })}`,
    breadcrumb: me?.is_admin
      ? [
          {
            title: intl.formatMessage({ id: 'SUBMENU.COMPANIES' }),
            root: true,
            page: `companies/list/${
              values?.roles === 'ROLE_BUYER' ? UserListType.BUYER : UserListType.VENDOR
            }`,
            translate: 'SUBMENU.COMPANIES',
            click: () => clearPagination(),
          },
        ]
      : [],
  });
  setLayoutFooter({ show: true });

  useEffect(() => {
    if (byIdSuccess && company) {
      setValues(getInitialValues(company));
    }
  }, [byIdSuccess, company, setValues]);

  useEffect(() => {
    if (loadingMe) return;

    if (me && !isManager && !me?.is_admin && !me?.company && companyId) {
      navigate('/companies/list');
    }
  }, [me, loadingMe]);

  useEffect(() => {
    if (byIdSuccess && company) {
      setValues(getInitialValues(company));
    }
  }, [byIdSuccess, company, setValues]);

  const handleDeleteDialog = useCallback((id: number) => {
    setDeleteStoreId(id);
    setOpenDeleteStoreAlert(true);
  }, []);

  const handleUnConnectUserDialog = useCallback((id: number) => {
    setIsOpenUnconnectUserAlert(true);
    setUnconnectUserId(id);
  }, []);

  const handleUnConnectUser = useCallback(() => {
    setIsOpenUnconnectUserAlert(false);

    companyId &&
      edit({
        id: +companyId,
        data: {
          managers_ids: company?.managers
            .filter(manager => manager.id !== unconnectUserId)
            .map(i => i.id),
        },
      });
    setUnconnectUserId(-1);
  }, [companyId, company, unconnectUserId]);

  const deleteStoreAction = useCallback(() => {
    setOpenDeleteStoreAlert(false);
    if (deleteStoreId) {
      delStore({ id: deleteStoreId, page: 1, perPage: 10 });
    }
  }, [deleteStoreId, delStore]);

  const handleConnectUser = useCallback(
    (userId?: number, companyProp?: ICompany) => {
      const managers = companyProp?.managers ? companyProp?.managers.map(i => i.id) : [];
      companyId &&
        edit({
          id: companyProp ? companyProp.id : +companyId,
          data: {
            managers_ids: [...managers, userId || 0],
          },
        });
      setOpenUsersDialog(false);
    },
    [company?.managers, companyId]
  );

  if (loading || loadingMe || loadingStores) return <Preloader />;

  const FIELDS: IField<keyof typeof values>[] = [
    { title: intl.formatMessage({ id: 'COMPANY.FORM.LEGAL_PERSON' }), field: 'name' },
    { title: intl.formatMessage({ id: 'COMPANY.FORM.BRAND' }), field: 'brand' },
    { title: intl.formatMessage({ id: 'COMPANY.FORM.INN' }), field: 'inn' },
    { title: intl.formatMessage({ id: 'COMPANY.FORM.PHONE' }), field: 'phone_number' },
    { title: intl.formatMessage({ id: 'COMPANY.FORM.SITE' }), field: 'site' },
  ];

  return (
    <>
      <AlertDialog
        open={isOpenDeleteStoreAlert}
        message={intl.formatMessage({ id: 'STORE.DELETE.TEXT' })}
        okText={intl.formatMessage({ id: 'CATEGORIES.DELETE.OK' })}
        cancelText={intl.formatMessage({ id: 'CATEGORIES.DELETE.CANCEL' })}
        handleClose={() => {
          clearDelStore();
          setOpenDeleteStoreAlert(false);
        }}
        handleAgree={deleteStoreAction}
      />

      <AlertDialog
        open={isOpenUnconnectUserAlert}
        message={intl.formatMessage({ id: 'COMPANY.EDIT.UNMOUNT_USER_TEXT' })}
        okText={intl.formatMessage({ id: 'CATEGORIES.DELETE.OK' })}
        cancelText={intl.formatMessage({ id: 'CATEGORIES.DELETE.CANCEL' })}
        handleClose={() => {
          setIsOpenUnconnectUserAlert(false);
          setUnconnectUserId(-1);
        }}
        handleAgree={handleUnConnectUser}
      />
      <Row>
        <Col>
          <Card className={homeClasses.classes.container}>
            <div className={homeClasses.classes.headerGradient} />
            <>
              <Tabs
                value={valueTabs}
                onChange={(_: React.ChangeEvent<{}>, newValue: number) => {
                  if (newValue !== 0 && !companyId) {
                    handleChangeTabPhoto(newValue);
                  } else {
                    setValueTabs(newValue);
                    setLocalTabs(newValue);
                  }
                }}
                variant='scrollable'
                indicatorColor='primary'
                textColor='primary'
                aria-label='tabs'
              >
                <Tab
                  label={intl.formatMessage({ id: 'COMPANY.EDIT.TAB_COMPANY' })}
                  {...a11yProps(0)}
                />
                {(me?.is_admin || isManager) && !isBuyerCompany && (
                  <Tab
                    label={intl.formatMessage({ id: 'COMPANY.EDIT.TAB_STORES' })}
                    {...a11yProps(1)}
                  />
                )}
                {companyId && (me?.is_admin || isManager || isBuyer || isVendor) && (
                  <Tab
                    label={intl.formatMessage({ id: 'COMPANY.EDIT.TAB_MANAGERS' })}
                    {...a11yProps(2)}
                  />
                )}
                {companyId && (me?.is_admin || isManager || isBuyer || isVendor) && (
                  <Tab
                    label={intl.formatMessage({ id: 'COMPANY.EDIT.TAB_PAYMENTS' })}
                    {...a11yProps(3)}
                  />
                )}
                {companyId && isVendor && (
                  <Tab
                    label={intl.formatMessage({ id: 'YOUR.COMMISSION' })}
                    {...a11yProps(3)}
                  />
                )}
              </Tabs>
              {!(companyId && isVendor && valueTabs === 3) && (
                <Typography
                  variant='h2'
                  className={classes.title}
                  style={{ width: '100%', paddingLeft: 24 }}
                >
                  {intl.formatMessage({ id: 'COMPANY.PROFILE' })}
                </Typography>
              )}
              {valueTabs === 0 && (
                <form
                  className={homeClasses.classes.form}
                  onSubmit={handleSubmit}
                  autoComplete='off'
                >
                  <div className={classes.fieldsContainer}>
                    <div className={classes.fields}>
                      {FIELDS.map(item => {
                        return item.field === 'phone_number' ? (
                          <div className={classes.dates}>
                            <div className={classes.country}>
                              <CountrySelect
                                label={intl.formatMessage({ id: 'USER.INPUT.COUNTRY_CODE' })}
                                countries={countries}
                                countryId={countryId}
                                setCountryId={(id: number) => {
                                  setCountryId(id);
                                  setFieldValue('phone_number', '');
                                }}
                                countriesLoading={countriesLoading}
                                disabled={!!isBuyerStaff || !!isVendorStaff}
                                showCode
                                variant='outlined'
                              />
                            </div>
                            {countryData && (
                              <IMaskPhoneInput
                                mask={countryData.mask}
                                onBlur={handleBlur}
                                value={values.phone_number}
                                onChange={(e: any) => {
                                  setFieldValue(
                                    'phone_number',
                                    e.target.value ? e.target.value.replace(/[^0-9]/g, '') : ''
                                  );
                                }}
                                type='tel'
                                label={intl.formatMessage({ id: 'AUTH.INPUT.PHONE' })}
                                margin='normal'
                                className='kt-width-full'
                                name='phone_number'
                                helperText={touched.phone_number && errors.phone_number}
                                error={Boolean(touched.phone_number && errors.phone_number)}
                                variant='outlined'
                                disabled={!!isBuyerStaff || !!isVendorStaff}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position='start'>
                                      {' '}
                                      +{countryData.code}{' '}
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            )}
                          </div>
                        ) : (
                          <TextField
                            key={item.field}
                            type='text'
                            label={item.title}
                            margin='normal'
                            name={item.field}
                            value={values[item.field]}
                            variant='outlined'
                            onBlur={handleBlur}
                            disabled={!!isBuyerStaff || !!isVendorStaff}
                            onChange={handleChange}
                            // @ts-ignore
                            helperText={touched[item.field] && errors[item.field]}
                            error={Boolean(touched[item.field] && errors[item.field])}
                            // style={{ maxWidth: 500 }}
                            // className={classes.fields}
                          />
                        );
                      })}
                      <div style={{ marginTop: 10 }}>
                        <InputLabel shrink>
                          {intl.formatMessage({ id: 'PRODUCT.TABLE.DESCRIPTION' })}
                        </InputLabel>
                        <CkEditorCustom
                          html={company?.description || ''}
                          setEditorStateCallback={setEditorState}
                          disabled={!!isBuyerStaff || !!isVendorStaff}
                          className={classes.editorContainer}
                        />
                      </div>
                    </div>
                    <div className={classes.fields}>
                      {values.roles === UserRoles.ROLE_VENDOR && (
                        <TextField
                          type='number'
                          margin='normal'
                          label={fm('FEE')}
                          value={values?.fee}
                          onChange={handleChange}
                          name='fee'
                          variant='outlined'
                          helperText={touched.fee && errors.fee}
                          error={Boolean(touched.fee && errors.fee)}
                          disabled={!me?.is_admin}
                        />
                      )}
                      {values.roles === UserRoles.ROLE_VENDOR && (
                        <TextField
                          type='number'
                          margin='normal'
                          label={fm('DELIVERY.PERIOD')}
                          value={values?.deliveryPeriod}
                          onChange={handleChange}
                          name='deliveryPeriod'
                          variant='outlined'
                          helperText={touched.deliveryPeriod && errors.deliveryPeriod}
                          error={Boolean(touched.deliveryPeriod && errors.deliveryPeriod)}
                          disabled={!me?.is_admin && !isVendor}
                        />
                      )}
                      <TextField
                        select
                        margin='normal'
                        label={fm('USER.EDIT.ROLE')}
                        value={values?.roles || ''}
                        onChange={handleChange}
                        name='roles'
                        variant='outlined'
                        helperText={touched.roles && errors.roles}
                        error={Boolean(touched.roles && errors.roles)}
                        disabled={!!companyId || (!me?.is_admin && !isManager)}
                      >
                        <MenuItem value={UserRoles.ROLE_VENDOR}>
                          {fm('COMPANY.ROLES.VENDOR')}
                        </MenuItem>
                        <MenuItem value={UserRoles.ROLE_BUYER}>
                          {fm('COMPANY.ROLES.BUYER')}
                        </MenuItem>
                      </TextField>
                      <div className={classes.actions}>
                        {!isBuyerStaff && !isVendorStaff && (
                          <div className={`${classes.actions} ${classes.webActions}`}>
                            {companyId && me?.is_admin && (
                              <Button
                                onClick={() => setOpen(true)}
                                className={classes.btnDel}
                                variant='contained'
                                color='error'
                                style={{ marginRight: 15 }}
                                // fullWidth
                              >
                                {intl.formatMessage({ id: 'COMMON.BUTTON.DELETE' })}
                              </Button>
                            )}
                            <Button
                              style={{ backgroundColor: '#006997' }}
                              type='submit'
                              variant='contained'
                              color='primary'
                              // fullWidth
                            >
                              {companyId
                                ? intl.formatMessage({ id: 'USER.BUTTON.EDIT' })
                                : intl.formatMessage({ id: 'USER.BUTTON.ADD' })}
                            </Button>
                          </div>
                        )}

                        <div className={`${classes.actions} ${classes.mobileActions}`}>
                          {companyId && me?.is_admin && (
                            <Button
                              onClick={() => setOpen(true)}
                              className={classes.btnDel}
                              variant='contained'
                              color='error'
                              fullWidth
                              style={{ marginTop: 24 }}
                            >
                              {intl.formatMessage({ id: 'COMMON.BUTTON.DELETE' })}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              )}

              {(me?.is_admin || isManager) && !isBuyerCompany && (
                <TabPanel
                  value={valueTabs}
                  index={1}
                  style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                >
                  <div className={homeClasses.classes.form} style={{ margin: '0 auto' }}>
                    <div className={classes.table}>
                      <div className={classes.rootTable}>
                        <div className='actions'>
                          <Button
                            color='primary'
                            variant='contained'
                            onClick={() =>
                              navigate(`/stores/create/${companyId}/${company?.name}`)
                            }
                          >
                            {intl.formatMessage({ id: 'STORE.BUTTON.ADD' })}
                          </Button>
                        </div>

                        <div>
                          <StoresTable
                            stores={stores || []}
                            loading={loadingStores}
                            paginationData={{
                              page: pageStores,
                              perPage: perPageStores,
                              total: totalStores,
                            }}
                            isAdmin={!!me?.is_admin || !!isManager}
                            onEdit={storeId => navigate(`/stores/edit/${storeId}`)}
                            onDelete={storeId => handleDeleteDialog(storeId)}
                            fetch={({ page, perPage }) =>
                              companyId &&
                              fetchStores({ page, perPage, companyId: +companyId })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </TabPanel>
              )}
              {companyId && (me?.is_admin || isManager || isBuyer || isVendor) && (
                <TabPanel
                  value={valueTabs}
                  index={isBuyerCompany || isBuyer || isVendor ? 1 : 2}
                  style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                >
                  <div className={homeClasses.classes.form} style={{ margin: '0 auto' }}>
                    <div className={classes.table}>
                      <div className={classes.rootTable}>
                        {(me?.is_admin || isBuyer || isVendor) && (
                          <Button
                            color='primary'
                            variant='contained'
                            onClick={() =>
                              navigate(
                                `/users/invite/${
                                  company?.roles?.includes(BUYER_ROLE)
                                    ? UserListType.BUYER_STAFF_ROLE
                                    : UserListType.STAFF
                                }`,
                                { state: { company } }
                              )
                            }
                          >
                            {intl.formatMessage({ id: 'INVITE' })}
                          </Button>
                        )}

                        <UsersTable
                          owner={company?.owner}
                          users={
                            company?.managers
                              ? company?.managers.reduce((acc: IUser[], item: IUser) => {
                                  item.roles.includes('ROLE_VENDOR')
                                    ? acc.unshift(item)
                                    : acc.push(item);
                                  return acc;
                                }, [])
                              : []
                          }
                          loading={loading}
                          paginationData={{ page: 1, perPage: 10, total: 10 }}
                          onEdit={userId =>
                            navigate(`/users/edit/${UserListType.MANAGER}/${userId}`)
                          }
                          hideRole={!me?.is_admin}
                          // editManagerRole={editManagerRole}
                          selectedRole={
                            !!me?.is_admin || !!isManager || !!isBuyer || !!isVendor
                          }
                          onConnectOff={userId => handleUnConnectUserDialog(userId)}
                          // hideActions={!me?.is_admin }
                          showEdit={!!me?.is_admin || !!isManager}
                        />
                      </div>
                    </div>
                  </div>
                </TabPanel>
              )}

              <TabPanel
                value={valueTabs}
                index={isBuyerCompany || isBuyer || isVendor ? 2 : 3}
              >
                <div className={homeClasses.classes.form} style={{ margin: '0 auto' }}>
                  <div style={{ marginTop: 10 }}>
                    <TextField
                      type='text'
                      label='ОГРН'
                      margin='normal'
                      name='requisites.ogrn'
                      value={values.requisites.ogrn}
                      variant='outlined'
                      onBlur={handleBlur}
                      disabled={!!isBuyerStaff || !!isVendorStaff}
                      onChange={handleChange}
                    />
                    <TextField
                      type='text'
                      label={fm('SUBMENU.COMPANY')}
                      margin='normal'
                      name='requisites.legal_entity'
                      value={values.requisites.legal_entity || ''}
                      variant='outlined'
                      disabled={!!isBuyerStaff || !!isVendorStaff}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                    <TextField
                      type='text'
                      label='Юр.адрес'
                      margin='normal'
                      name='requisites.legal_address'
                      value={values.requisites.legal_address}
                      variant='outlined'
                      onBlur={handleBlur}
                      disabled={!!isBuyerStaff || !!isVendorStaff}
                      onChange={handleChange}
                    />
                    <TextField
                      type='text'
                      label={fm('POST.ADDRESS')}
                      margin='normal'
                      name='requisites.post_address'
                      value={values.requisites.post_address}
                      variant='outlined'
                      onBlur={handleBlur}
                      disabled={!!isBuyerStaff || !!isVendorStaff}
                      onChange={handleChange}
                    />
                    <TextField
                      type='text'
                      label={fm('PAYMENT.ACCOUNT')}
                      margin='normal'
                      name='requisites.payment_account'
                      value={values.requisites.payment_account}
                      variant='outlined'
                      onBlur={handleBlur}
                      disabled={!!isBuyerStaff || !!isVendorStaff}
                      onChange={handleChange}
                    />
                    <TextField
                      type='text'
                      label='Название банка'
                      margin='normal'
                      name='requisites.bank'
                      value={values.requisites.bank}
                      variant='outlined'
                      onBlur={handleBlur}
                      disabled={!!isBuyerStaff || !!isVendorStaff}
                      onChange={handleChange}
                    />
                    <TextField
                      type='text'
                      label='БИК'
                      margin='normal'
                      name='requisites.bank_bic'
                      value={values.requisites.bank_bic}
                      variant='outlined'
                      onBlur={handleBlur}
                      disabled={!!isBuyerStaff || !!isVendorStaff}
                      onChange={handleChange}
                    />
                    <TextField
                      type='text'
                      label='К/с'
                      margin='normal'
                      name='requisites.correspondent_account'
                      value={values.requisites.correspondent_account}
                      variant='outlined'
                      onBlur={handleBlur}
                      disabled={!!isBuyerStaff || !!isVendorStaff}
                      onChange={handleChange}
                    />
                    <TextField
                      type='text'
                      label={fm('KPP')}
                      margin='normal'
                      name='requisites.kpp'
                      value={values.requisites.kpp}
                      variant='outlined'
                      onBlur={handleBlur}
                      disabled={!!isBuyerStaff || !!isVendorStaff}
                      onChange={handleChange}
                    />

                    <TextField
                      type='text'
                      label={fm('OWNER')}
                      margin='normal'
                      name='requisites.owner'
                      value={values.requisites.owner}
                      variant='outlined'
                      onBlur={handleBlur}
                      disabled={!!isBuyerStaff || !!isVendorStaff}
                      onChange={handleChange}
                    />
                    <TextField
                      type='text'
                      label={fm('REASON')}
                      margin='normal'
                      name='requisites.reason'
                      value={values.requisites.reason}
                      variant='outlined'
                      onBlur={handleBlur}
                      disabled={!!isBuyerStaff || !!isVendorStaff}
                      onChange={handleChange}
                    />
                    {companyId && (
                      <Button
                        className={classes.editButton}
                        type='submit'
                        variant='contained'
                        color='primary'
                        onClick={() => handleSubmit()}
                      >
                        {intl.formatMessage({ id: 'USER.BUTTON.EDIT' })}
                      </Button>
                    )}
                  </div>
                </div>
              </TabPanel>
              {isVendor && valueTabs === 3 && (
                <div className={homeClasses.classes.form}>
                  <Typography variant='h2' className={classes.title}>
                    {company?.fee ? `${company?.fee}%` : ''}
                  </Typography>
                  <div dangerouslySetInnerHTML={{ __html: userDocument?.text || '' }} />
                </div>
              )}
            </>
          </Card>
        </Col>
      </Row>

      <Modal
        DialogProps={{ maxWidth: 'md' } as DialogProps}
        open={openUsersDialog}
        onClose={() => setOpenUsersDialog(false)}
        title={intl.formatMessage({ id: 'COMPANY.EDIT.MOUNT_USER_TITLE' })}
        loading={loadingUsers}
        content={
          <UsersTable
            loading={loadingUsers}
            paginationData={{ page: pageUsers, perPage: perPageUsers, total: totalUsers }}
            onEdit={userId => navigate(`/users/edit/${UserListType.MANAGER}/${userId}`)}
            onConnect={userId => handleConnectUser(userId, company || undefined)}
            onConnectOff={userId => handleUnConnectUserDialog(userId)}
            connectedUsers={company?.managers.map(item => item.id)}
            users={users}
            showEdit={!!me?.is_admin || !!isManager}
            fetch={({ page, perPage }) =>
              fetchUsers({
                per_page: perPage,
                page,
                roles: 'ROLE_VENDOR,ROLE_VENDOR_STAFF,ROLE_BUYER,ROLE_BUYER_STAFF',
                filter,
              })
            }
          />
        }
        actions={[
          {
            title: intl.formatMessage({ id: 'COMMON.BUTTON.CLOSE' }),
            onClick: () => setOpenUsersDialog(false),
          },
        ]}
      />

      <Modal
        open={open}
        onClose={delCompanyLoading ? () => {} : () => setOpen(false)}
        title={intl.formatMessage({ id: 'COMPANY.DELETE.TITLE' })}
        loading={delCompanyLoading}
        content={
          delCompanyLoading ? undefined : intl.formatMessage({ id: 'COMPANY.DELETE.TEXT' })
        }
        actions={
          delCompanyLoading
            ? undefined
            : [
                {
                  title: intl.formatMessage({ id: 'COMMON.BUTTON.CANCEL' }),
                  onClick: () => setOpen(false),
                },
                {
                  title: intl.formatMessage({ id: 'COMMON.BUTTON.DELETE' }),
                  onClick: () => companyId && delCompany(+companyId),
                },
              ]
        }
      />

      <Modal
        DialogProps={{ maxWidth: 'md' } as DialogProps}
        open={openDialogCompanies}
        onClose={editLoading ? () => {} : () => setOpenDialogCompanies(false)}
        title={intl.formatMessage({ id: 'COMPANY.EDIT.MOUNT_TITLE' })}
        loading={editLoading || loadingCompanies}
        content={
          <CompaniesTable
            me={me as IUser}
            page={pageCompanies}
            perPage={perPageCompanies}
            total={totalCompanies}
            onConnectUser={companyProp => handleConnectUser(me?.id, companyProp)}
            companies={companies}
          />
        }
        actions={[
          {
            title: intl.formatMessage({ id: 'COMMON.BUTTON.CLOSE' }),
            onClick: () => setOpenDialogCompanies(false),
          },
        ]}
      />
    </>
  );
};

const connector = connect(
  (state: IAppState) => ({
    me: state.profile.me,
    loadingMe: state.profile.loading,

    company: state.companies.company,
    loading: state.companies.byIdLoading,
    editSuccess: state.companies.editSuccess,
    editLoading: state.companies.editLoading,
    editError: state.companies.editError,
    byIdSuccess: state.companies.byIdSuccess,
    delCompanyLoading: state.companies.delCompanyLoading,
    delCompanySuccess: state.companies.delCompanySuccess,
    delCompanyError: state.companies.delCompanyError,

    delStoreSuccess: state.stores.delSuccess,
    delStoreError: state.stores.delError,
    delStoreLoding: state.stores.delLoading,
    editStoreSucces: state.stores.editSuccess,
    editStoreError: state.stores.editError,

    companies: state.companies.companies,
    loadingCompanies: state.companies.searchLoading,
    pageCompanies: state.companies.page,
    perPageCompanies: state.companies.per_page,
    totalCompanies: state.companies.total,
    byIdError: state.companies.byIdError,

    users: state.users.users,
    loadingUsers: state.users.loading,
    perPageUsers: state.users.per_page,
    pageUsers: state.users.page,
    totalUsers: state.users.total,
    filter: state.users.filter,
    addSuccess: state.companies.addSuccess,
    addCompanyId: state.companies.addCompanyId,
    stores: state.stores.stores,
    loadingStores: state.stores.loading,
    pageStores: state.stores.page,
    perPageStores: state.stores.per_page,
    totalStores: state.stores.total,
  }),
  {
    fetchMe: profileActions.fetchRequest,

    fetch: companiesActions.fetchByIdRequest,
    clearEdit: companiesActions.clearEdit,
    clearDelCompany: companiesActions.clearDelCompany,
    add: companiesActions.addRequest,
    edit: companiesActions.editRequest,
    delCompany: companiesActions.delCompanyRequest,
    clearFetch: companiesActions.clearFetch,
    searchCompany: companiesActions.searchRequest,

    delStore: storeActions.delRequest,
    clearDelStore: storeActions.clearDel,

    fetchUsers: usersActions.fetchRequest,
    clearPagination: companiesActions.clearPagination,
    clearAddCompanyId: companiesActions.clearAddCompanyId,
    fetchStores: storeActions.fetchRequest,
    clearStores: storeActions.clearStores,
    clearFetchById: companiesActions.clearFetchById,
  }
);

type TPropsFromRedux = ConnectedProps<typeof connector>;

export default connector(CompanyEditPage);
